import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SimplePeer from 'simple-peer';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function CallRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [copied, setCopied] = useState(false);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<SimplePeer.Instance | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  // Initialize media stream
  useEffect(() => {
    const initMedia = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
        setStream(mediaStream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        toast.error('Camera/microphone access denied');
      }
    };
    initMedia();

    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // Initialize WebRTC peer connection (simplified signaling)
  useEffect(() => {
    if (!stream) return;

    const peer = new SimplePeer({
      initiator: Math.random() > 0.5,
      trickleIce: true,
      stream,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      },
    });

    peer.on('stream', (remoteStream) => {
      setRemoteStream(remoteStream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
    });

    peer.on('error', (err) => {
      console.error('Peer error:', err);
      toast.error('Connection error');
    });

    peerRef.current = peer;

    return () => {
      peer.destroy();
    };
  }, [stream]);

  const toggleMic = () => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  const endCall = () => {
    stream?.getTracks().forEach((track) => track.stop());
    peerRef.current?.destroy();
    window.location.href = '/';
  };

  const copyLink = () => {
    const link = `${window.location.origin}/call/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="h-screen w-full bg-slate-950 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-white font-medium">Live Battle</span>
          <span className="text-xs text-slate-500 font-mono">{roomId}</span>
        </div>
        <Button
          onClick={copyLink}
          variant="outline"
          size="sm"
          className="border-slate-700 hover:bg-slate-800"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied' : 'Share'}
        </Button>
      </div>

      {/* Video Grid */}
      <div className="flex-1 grid grid-cols-2 gap-1 p-4 overflow-hidden">
        {/* Local Video */}
        <div className="relative bg-slate-900 rounded-xl overflow-hidden">
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-3 left-3 text-xs font-medium text-white bg-slate-900/70 px-2 py-1 rounded">
            You
          </div>
          {isVideoOff && (
            <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
              <VideoOff className="w-12 h-12 text-slate-600" />
            </div>
          )}
        </div>

        {/* Remote Video */}
        <div className="relative bg-slate-900 rounded-xl overflow-hidden">
          {remoteStream ? (
            <>
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 text-xs font-medium text-white bg-slate-900/70 px-2 py-1 rounded">
                Opponent
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Video className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">Waiting for connection...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 px-6 py-6 border-t border-slate-800 bg-slate-900/50">
        <Button
          onClick={toggleMic}
          size="lg"
          className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${
            isMuted
              ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
          }`}
          variant="ghost"
        >
          {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </Button>

        <Button
          onClick={toggleVideo}
          size="lg"
          className={`rounded-full w-14 h-14 flex items-center justify-center transition-colors ${
            isVideoOff
              ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
          }`}
          variant="ghost"
        >
          {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
        </Button>

        <Button
          onClick={endCall}
          size="lg"
          className="rounded-full w-14 h-14 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white"
          variant="ghost"
        >
          <PhoneOff className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
}
